import React from 'react'
import { Box, Skeleton, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { C, cardSx, tabular } from '../RestaurantDashboard/tokens'
import { cardSubSx, cardTitleSx } from '../RestaurantDashboard/SalesOverTime'
import { $, NOT_TRACKED, pct, pctOf, rate } from './saUtil'

/* ── ROW 2: order money breakdown ─────────────────────────────────────── */

function Tile({ label, value, share, cur, prev, badWhenUp, note, tracked = true, loading }) {
  let deltaEl = null
  if (tracked && prev != null && cur != null) {
    const diff = Number(cur) - Number(prev)
    if (Math.abs(diff) >= 1) {
      const up = diff > 0
      const bad = badWhenUp ? up : !up
      const d = prev ? (diff / Math.abs(prev)) * 100 : null
      deltaEl = (
        <Box component="span" sx={{ ...tabular, fontSize: 11.5, fontWeight: 600, color: bad ? C.downText : C.upText, whiteSpace: 'nowrap' }}>
          {up ? '▲' : '▼'} {d == null ? 'new' : pct(d)}
        </Box>
      )
    }
  }
  return (
    <Box sx={{ ...cardSx, px: 2, py: 1.5, minHeight: 104, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: C.textMuted }}>{label}</Typography>
        {note ? (
          <Tooltip title={note} arrow enterTouchDelay={0}>
            <InfoOutlinedIcon tabIndex={0} aria-label={note} sx={{ fontSize: 14, color: C.textMuted, cursor: 'help' }} />
          </Tooltip>
        ) : null}
      </Box>
      {loading ? <Skeleton width="60%" sx={{ fontSize: 20 }} /> : tracked ? (
        <Typography sx={{ ...tabular, fontSize: 20, fontWeight: 600, color: C.text, lineHeight: 1.2 }}>{value}</Typography>
      ) : (
        <Tooltip title={NOT_TRACKED} arrow>
          <Typography tabIndex={0} aria-label={`${label}: ${NOT_TRACKED}`} sx={{ fontSize: 20, fontWeight: 600, color: C.textMuted, lineHeight: 1.2, alignSelf: 'flex-start', cursor: 'help' }}>—</Typography>
        </Tooltip>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto', flexWrap: 'wrap' }}>
        <Typography sx={{ ...tabular, fontSize: 11.5, color: C.textMuted }}>{tracked ? `${share} of GMV` : NOT_TRACKED}</Typography>
        {deltaEl}
      </Box>
    </Box>
  )
}

export function MoneyGrid({ data, loading }) {
  const c = data?.current
  const p = data?.previous
  const gmv = c?.grandTotal || 0
  const tile = (key, label, opts = {}) => (
    <Tile key={key} label={label} loading={loading && !data}
      value={opts.value || $(c?.[key])} share={pctOf(c?.[key], gmv)} cur={c?.[key]} prev={p ? p[key] : null} {...opts} />
  )
  const driverTracked = c ? c.deliveryOrdersWithPayout > 0 : true
  const splitNote = c && c.ordersWithSplit < c.orders ? `Covers ${c.ordersWithSplit} of ${c.orders} orders — the rest have no split record.` : null
  return (
    <Box component="section" aria-label="Order money breakdown" sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' } }}>
      {tile('itemSubtotal', 'Item subtotal', { note: 'Menu items before any discount.' })}
      {tile('discounts', 'Discounts', {
        badWhenUp: true,
        note: c ? `Restaurant-funded ${$(c.discountsRestaurant)} · Platform-funded ${$(c.discountsPlatform)} (promotions with no restaurant, loyalty coupons, Black Card, wallet credit). The payment split still charges every discount to the restaurant.` : null
      })}
      {tile('deliveryFees', 'Delivery fees')}
      {tile('serviceFees', 'Service fees')}
      {tile('tax', 'Tax collected')}
      {tile('driverTips', 'Driver tips', { note: c ? `All tips ${$(c.tips)} — the rest are server tips.` : null })}
      {tile('commission', 'Commission earned', {
        note: [c && c.commissionRate != null ? `Average effective rate ${rate(c.commissionRate)}.` : null, splitNote].filter(Boolean).join(' ') || null
      })}
      {tile('driverPayouts', 'Driver payouts', {
        tracked: driverTracked,
        badWhenUp: true,
        note: c && driverTracked && c.deliveryOrdersWithPayout < c.deliveryOrders ? `Covers ${c.deliveryOrdersWithPayout} of ${c.deliveryOrders} deliveries (rider earnings + First Delivery cost).` : 'Rider earnings plus what First Delivery billed. First Delivery cost is recorded from now on.'
      })}
      {tile('refunds', 'Refunds', { badWhenUp: true, note: c ? `Platform absorbed ${$(c.refundsPlatform)}; the rest was clawed back from restaurants.` : null })}
      {tile('processingFees', 'Processing fees', {
        badWhenUp: true,
        tracked: c ? c.ordersWithProcessingFee > 0 || c.processingFees > 0 : true,
        note: 'Stripe’s actual fee per charge. QorCommerce’s fee is not recorded.'
      })}
    </Box>
  )
}

/* ── ROW 3: where the money goes ──────────────────────────────────────── */

const STEP = { total: '#111827', minus: '#DC2626', plus: '#10B981' }
const SPLIT = [
  { key: 'restaurantPayouts', label: 'Restaurant payouts', color: '#ED1D26', note: 'Restaurants’ share, including any tax or tip they keep' },
  { key: 'driverPayouts', label: 'Driver payouts + tips', color: '#F59E0B', note: 'Delivery tips the platform passes on, plus recorded driver pay' },
  { key: 'taxRemitted', label: 'Tax (remitted)', color: '#0EA5E9', note: 'Tax the platform keeps and remits' },
  { key: 'processingFees', label: 'Processing fees', color: '#6B7280', note: 'Stripe’s fee' },
  { key: 'platformNet', label: 'Platform net', color: '#10B981', note: 'What the platform keeps after the parts above' },
  { key: 'noSplit', label: 'No split record', color: '#CBD5E1', note: 'In-store checks and unsettled cash orders — nothing records who keeps what' }
]

export function Waterfall({ data, loading }) {
  const c = data?.current
  const w = data?.waterfall
  const gmv = c?.grandTotal || 0
  const steps = c ? [
    { label: 'Item subtotal', kind: 'total', from: 0, to: c.itemSubtotal, v: c.itemSubtotal },
    { label: '− Discounts', kind: 'minus', v: -c.discounts },
    { label: '+ Delivery fees', kind: 'plus', v: c.deliveryFees },
    { label: '+ Service fees', kind: 'plus', v: c.serviceFees },
    { label: '+ Tax', kind: 'plus', v: c.tax },
    { label: '+ Tips', kind: 'plus', v: c.tips },
    { label: '= Grand total', kind: 'total', from: 0, to: c.grandTotal, v: c.grandTotal }
  ] : []
  // Running position for the floating steps.
  let run = 0
  for (const s of steps) {
    if (s.kind === 'total') { run = s.to; continue }
    s.from = Math.min(run, run + s.v)
    s.to = Math.max(run, run + s.v)
    run += s.v
  }
  const max = Math.max(1, ...steps.map(s => s.to))
  const parts = w ? SPLIT.map(s => ({ ...s, v: w[s.key] || 0 })) : []
  const allPositive = parts.every(p => p.v >= 0)

  return (
    <Box sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
      <Typography component="h2" sx={cardTitleSx}>Where the money goes</Typography>
      <Typography sx={cardSubSx}>From what was ordered to what customers paid, then where that grand total ends up</Typography>
      {loading && !data ? <Skeleton variant="rounded" height={300} sx={{ mt: 2, borderRadius: '8px' }} /> : !c || !gmv ? (
        <Typography sx={{ fontSize: 13, color: C.textMuted, mt: 2 }}>No orders in this range.</Typography>
      ) : (
        <>
          <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: { xs: '110px 1fr 84px', sm: '150px 1fr 110px' }, rowGap: 1, columnGap: 1.5, alignItems: 'center' }} role="list" aria-label="From item subtotal to grand total">
            {steps.map(s => (
              <React.Fragment key={s.label}>
                <Typography role="listitem" sx={{ fontSize: 13, color: s.kind === 'total' ? C.text : C.textMuted, fontWeight: s.kind === 'total' ? 600 : 400 }}>{s.label}</Typography>
                <Box sx={{ position: 'relative', height: 22, bgcolor: '#F6F7F9', borderRadius: '4px' }}>
                  <Tooltip title={`${$(Math.abs(s.v))} · ${pctOf(Math.abs(s.v), gmv)} of grand total`} arrow followCursor>
                    <Box sx={{
                      position: 'absolute', top: 3, bottom: 3, borderRadius: '3px',
                      left: `${(s.from / max) * 100}%`, width: `${Math.max(0.4, ((s.to - s.from) / max) * 100)}%`,
                      bgcolor: STEP[s.kind]
                    }} />
                  </Tooltip>
                </Box>
                <Typography sx={{ ...tabular, fontSize: 13, textAlign: 'right', fontWeight: s.kind === 'total' ? 600 : 500, color: s.kind === 'minus' ? C.downText : C.text }}>
                  {s.kind === 'minus' ? `−${$(Math.abs(s.v))}` : $(s.v)}
                </Typography>
              </React.Fragment>
            ))}
          </Box>

          <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text, mt: 3, mb: 1 }}>Grand total {$(gmv)} splits into</Typography>
          {allPositive ? (
            <Box sx={{ display: 'flex', height: 18, borderRadius: '6px', overflow: 'hidden', bgcolor: '#F1F2F4' }} role="img"
              aria-label={parts.map(p => `${p.label} ${$(p.v)}`).join(', ')}>
              {parts.filter(p => p.v > 0).map((p, i) => (
                <Tooltip key={p.key} title={`${p.label}: ${$(p.v)} · ${pctOf(p.v, gmv)} of grand total`} arrow followCursor>
                  <Box sx={{ width: `${(p.v / gmv) * 100}%`, bgcolor: p.color, borderLeft: i ? '2px solid #fff' : 0 }} />
                </Tooltip>
              ))}
            </Box>
          ) : null}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', xl: 'repeat(6, 1fr)' }, gap: 1.5, mt: 1.5 }}>
            {parts.filter(p => p.v !== 0 || p.key !== 'noSplit').map(p => (
              <Box key={p.key} sx={{ display: 'flex', gap: 1 }} title={p.note}>
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: p.color, mt: 0.5, flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontSize: 12, color: C.textMuted }}>{p.label}</Typography>
                  <Typography sx={{ ...tabular, fontSize: 14, fontWeight: 600, color: p.v < 0 ? C.downText : C.text }}>{$(p.v)}</Typography>
                  <Typography sx={{ ...tabular, fontSize: 11.5, color: C.textMuted }}>{pctOf(p.v, gmv)}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
          <Typography sx={{ fontSize: 11.5, color: C.textMuted, mt: 1.5 }}>
            From the payment split records, so nothing is counted twice; the parts add up to the grand total. Driver payouts count only deliveries with a recorded cost.
          </Typography>
        </>
      )}
    </Box>
  )
}
